import { useState } from 'react';
import { useTranslation } from 'react-i18next';

type UseDialogCropImageParams = Readonly<{
  onClose: () => void;
  onSuccess: (image: string) => void;
}>;

type UseDialogCropImageResult = Readonly<{
  buttonTextSuccess: string;
  description: string;
  handleChange: (image: string) => void;
  handleDeleteImage: () => void;
  handleSuccess: () => void;
  metaInfo: string;
}>;

function useDialogCropImage({ onClose, onSuccess }: UseDialogCropImageParams): UseDialogCropImageResult {
  const { t } = useTranslation(['profile', 'actions']);
  // `currentImage` resets automatically on close because MUI `Modal` unmounts
  // its children when `keepMounted=false` (Dialog v2 default). If a consumer
  // ever opts into `keepMounted=true`, add:
  //   useEffect(() => { if (!isOpen) setCurrentImage(null); }, [isOpen]);
  const [currentImage, setCurrentImage] = useState<string | null>(null);

  function handleChange(image: string): void {
    setCurrentImage(image);
  }

  function handleDeleteImage(): void {
    setCurrentImage(null);
  }

  function handleSuccess(): void {
    if (currentImage) {
      onSuccess(currentImage);
    } else {
      onClose();
    }
  }

  return {
    buttonTextSuccess: t('actions:Save'),
    description: t('profile:Upload and drag the image to adjust position'),
    handleChange,
    handleDeleteImage,
    handleSuccess,
    metaInfo: t('profile:jpg/png'),
  };
}

export default useDialogCropImage;
